import { useState, type FormEvent } from 'react';
import { Eye, EyeOff } from 'lucide-react';

import { useAuth } from '../auth/AuthContext';
import { ApiError, authApi } from '../lib/api';
import { Logotype } from '../components/Wordmark';

/**
 * Forgot-password lives inside this card rather than on a page of its own:
 * it is one field, and the person asking is already here. The path is kept in
 * step so the "Reset my password" button in a password-changed email can open
 * straight onto it.
 */
type Mode = 'sign-in' | 'forgot' | 'sent';

const FORGOT_PATH = '/forgot-password';

function initialMode(): Mode {
  return window.location.pathname === FORGOT_PATH ? 'forgot' : 'sign-in';
}

/**
 * Sign-in.
 *
 * The one screen that is allowed to be about the brand rather than about the
 * figures: it is the first thing a new manager sees, and it has to look like the
 * company they bought from. So the logotype is set large, and the card carries
 * the gradient edge.
 *
 * Still deliberately nothing like the till's login. That screen is a full-bleed
 * neon scanner viewfinder for someone holding a barcode at a counter; this is
 * someone on a phone or a laptop, possibly not in the shop, typing a password.
 */
export function Login(): React.JSX.Element {
  const { status, sessionEnded, login, submitMfa } = useAuth();
  const awaitingMfa = status === 'awaiting-mfa';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [mode, setModeState] = useState<Mode>(initialMode);

  function setMode(next: Mode): void {
    setError(null);
    setModeState(next);
    const path = next === 'sign-in' ? '/' : FORGOT_PATH;
    if (window.location.pathname !== path) window.history.replaceState(null, '', path);
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setBusy(true);

    try {
      if (awaitingMfa) {
        await submitMfa(code);
      } else if (mode === 'forgot') {
        // The reply is the same whether or not the address has an account, so
        // there is nothing to branch on: it either reached the server or not.
        await authApi.forgotPassword(email);
        setMode('sent');
      } else {
        await login(email, password);
      }
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'Something went wrong. Try again.',
      );
      setCode('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-paper px-4 py-10">
      <div className="w-full max-w-[22rem]">
        <div className="mb-7 text-center">
          <Logotype size="lg" />
          <p className="qs-eyebrow mt-2">Cloud</p>
        </div>

        <div className="qs-panel overflow-hidden p-0">
          <div className="qs-brand-rule h-1" aria-hidden />
          <div className="p-5">
            <h1 className="text-lg font-medium tracking-tight">
              {awaitingMfa
                ? 'Two-factor code'
                : mode === 'forgot'
                  ? 'Reset your password'
                  : mode === 'sent'
                    ? 'Check your email'
                    : 'Sign in'}
            </h1>
            {awaitingMfa && (
              <p className="mt-1 text-xs text-ink-muted">
                Enter the six-digit code from your authenticator app.
              </p>
            )}
            {/* Not saying why: the server does not tell this tab, and "your
                access was removed" would be wrong for a session that expired. */}
            {!awaitingMfa && mode === 'sign-in' && sessionEnded && (
              <p role="status" className="mt-1 text-xs text-ink-muted">
                You were signed out. Sign in again to carry on.
              </p>
            )}
            {!awaitingMfa && mode === 'forgot' && (
              <p className="mt-1 text-xs text-ink-muted">
                Enter the email you sign in with and we will send you a link to choose a new
                password.
              </p>
            )}

          {!awaitingMfa && mode === 'sent' ? (
            <div className="mt-3 space-y-3">
              {/* Worded so it promises nothing about the account. The reply
                  is identical for an address with no account, and this text
                  has to be true either way. */}
              <p className="text-sm text-ink-muted">
                If <span className="wrap-anywhere text-ink">{email}</span> has a QuickStock
                account, a link to choose a new password is on its way. It can take a minute to
                arrive, so check your spam folder too.
              </p>
              <button
                type="button"
                onClick={() => setMode('sign-in')}
                className="w-full rounded border border-rule px-3 py-2 text-sm text-ink-muted hover:text-ink"
              >
                Back to sign in
              </button>
            </div>
          ) : (
          <form onSubmit={handleSubmit} className="mt-5 space-y-3">
            {awaitingMfa ? (
              <Field label="Code" htmlFor="code">
                <input
                  id="code"
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                  className="qs-figure w-full rounded border border-rule bg-surface px-2.5 py-2 text-center text-lg tracking-[0.4em] outline-none focus:border-accent"
                  // Numeric keypad on mobile, and the browser can fill it from an SMS.
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="000000"
                  autoFocus
                  required
                />
              </Field>
            ) : (
              <>
                <Field label="Email" htmlFor="email">
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    className="w-full rounded border border-rule bg-surface px-2.5 py-2 outline-none focus:border-accent"
                    autoComplete="username"
                    autoFocus
                    required
                  />
                </Field>

                {mode === 'sign-in' && (
                <Field label="Password" htmlFor="password">
                  {/*
                    A password typed on a phone keyboard, with no way to check
                    it, is a sign-in attempt spent on a typo — and the same
                    message comes back for a mistyped password as for the wrong
                    account, so there is nothing to tell you which it was.

                    `pr-10` reserves the eye's column so a long password runs
                    under the label rather than beneath the button. The button is
                    `tabIndex={-1}`: from the password field, Tab belongs to Sign
                    in, not to a control that only reveals what you just typed.
                  */}
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      className="w-full rounded border border-rule bg-surface py-2 pr-10 pl-2.5 outline-none focus:border-accent"
                      autoComplete="current-password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((shown) => !shown)}
                      tabIndex={-1}
                      aria-pressed={showPassword}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      className="qs-icon-button absolute inset-y-0 right-0 w-10 rounded text-ink-faint hover:text-ink"
                    >
                      {showPassword ? (
                        <EyeOff size={16} aria-hidden />
                      ) : (
                        <Eye size={16} aria-hidden />
                      )}
                    </button>
                  </div>
                </Field>
                )}
              </>
            )}

            {error && (
              // aria-live so it reaches a screen reader; the message from the
              // server is deliberately the same for every failure reason.
              <p role="alert" aria-live="polite" className="text-xs text-down">
                {error}
              </p>
            )}

            {/* The website's own call-to-action treatment: dark ink on the
                gradient, not white. White on the cyan end is 1.6:1 — the
                gradient is a light band, and only dark text survives both ends
                of it (11.4:1 on the cyan, 5.1:1 on the magenta). The literal
                near-black is deliberate: the ground here is a fixed gradient, so
                a theme-dependent ink would invert to white in dark mode and
                vanish. */}
            <button
              type="submit"
              disabled={busy}
              className="qs-brand-rule w-full rounded px-3 py-2 font-medium text-[#080d16] transition-opacity hover:opacity-90 disabled:opacity-60"
            >
              {busy
                ? mode === 'forgot'
                  ? 'Sending…'
                  : 'Checking…'
                : awaitingMfa
                  ? 'Verify'
                  : mode === 'forgot'
                    ? 'Email me a link'
                    : 'Sign in'}
            </button>

            {/* After the submit button, not beside the password field: from
                the password field, Tab belongs to Sign in. */}
            {!awaitingMfa && (
              <button
                type="button"
                onClick={() => setMode(mode === 'forgot' ? 'sign-in' : 'forgot')}
                className="block w-full py-1 text-center text-xs text-ink-muted hover:text-ink"
              >
                {mode === 'forgot' ? 'Back to sign in' : 'Forgot your password?'}
              </button>
            )}
          </form>
          )}
          </div>
        </div>

        <p className="mt-4 text-center text-2xs text-ink-faint">
          Accounts are created by an administrator.
        </p>
      </div>
    </div>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div>
      <label htmlFor={htmlFor} className="qs-eyebrow mb-1 block">
        {label}
      </label>
      {children}
    </div>
  );
}
